import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import {
  customerSupportOperations,
  customerRealtimeOperations,
  supabase,
} from '../../utils/customerSupabase';
import { useAuth } from '../../contexts/AuthContext';
import SupportChatbotContext from '../../contexts/SupportChatbotContext';

function formatTime(createdAt) {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

export function SupportChatbotWrapper({ children }) {
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const api = useMemo(
    () => ({
      openPanel: () => {
        setPanelOpen(true);
        setPendingOrderId(null);
      },
      openForOrder: (orderId) => {
        setPendingOrderId(orderId ?? null);
        setPanelOpen(true);
      },
    }),
    []
  );
  return (
    <SupportChatbotContext.Provider value={api}>
      {children}
      <SupportChatbotFloating
        forceOpen={panelOpen}
        pendingOrderId={pendingOrderId}
        onClose={() => {
          setPanelOpen(false);
          setPendingOrderId(null);
        }}
      />
    </SupportChatbotContext.Provider>
  );
}

function SupportChatbotFloating({ forceOpen, pendingOrderId, onClose }) {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasOpenThreads, setHasOpenThreads] = useState(true);
  const messagesEndRef = useRef(null);
  const threadIdRef = useRef(thread?.id);
  useEffect(() => { threadIdRef.current = thread?.id; }, [thread?.id]);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const refetchOpenThreads = useCallback(() => {
    if (!user?.id) return;
    customerSupportOperations.getOpenThreads(user.id).then(({ data: threads }) => {
      if (!threads?.length) {
        setHasOpenThreads(false);
        setThread(null);
        setMessages([]);
        setIsOpen(false);
        onClose?.();
      } else {
        setHasOpenThreads(true);
        const currentId = threadIdRef.current;
        const stillOpen = threads.find((t) => t.id === currentId);
        if (!stillOpen) {
          setThread(threads[0]);
          customerSupportOperations.getMessages(threads[0].id).then(({ data: msgs }) => setMessages(msgs ?? []));
          scrollToBottom();
        }
      }
    });
  }, [user?.id, onClose]);

  useEffect(() => {
    if (!user?.id || !isAuthenticated) {
      setThread(null);
      setMessages([]);
      setHasOpenThreads(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    customerSupportOperations.getOpenThreads(user.id).then(({ data: threads }) => {
      if (threads?.length > 0) {
        setHasOpenThreads(true);
        setThread(threads[0]);
        customerSupportOperations.getMessages(threads[0].id).then(({ data: msgs }) => {
          setMessages(msgs ?? []);
          setLoading(false);
          scrollToBottom();
        });
      } else {
        setHasOpenThreads(false);
        setThread(null);
        setMessages([]);
        setLoading(false);
      }
    });
  }, [user?.id, isAuthenticated]);

  useEffect(() => {
    if (forceOpen) setIsOpen(true);
  }, [forceOpen]);

  useEffect(() => {
    if (!isOpen || !user?.id || !pendingOrderId) return;
    customerSupportOperations.getOrCreateThread(user.id, pendingOrderId).then(({ data: t, error }) => {
      if (error || !t) return;
      setThread(t);
      setHasOpenThreads(true);
      customerSupportOperations.getMessages(t.id).then(({ data: msgs }) => {
        setMessages(msgs ?? []);
        scrollToBottom();
      });
    });
  }, [isOpen, user?.id, pendingOrderId]);

  useEffect(() => {
    if (!thread?.id) return;
    const ch1 = customerRealtimeOperations.subscribeToSupportThread(thread.id, (payload) => {
      if (payload?.new?.resolved_at) {
        refetchOpenThreads();
      }
    });
    const ch2 = customerRealtimeOperations.subscribeToSupportMessages(thread.id, () => {
      customerSupportOperations.getMessages(thread.id).then(({ data: msgs }) => setMessages(msgs ?? []));
    });
    return () => {
      supabase.removeChannel(ch1);
      supabase.removeChannel(ch2);
    };
  }, [thread?.id, refetchOpenThreads]);

  // Polling + visibility fallback: refetch when tab becomes visible or every 30s (handles Realtime not firing)
  useEffect(() => {
    if (!user?.id || !thread?.id) return;
    const onVisible = () => { if (document.visibilityState === 'visible') refetchOpenThreads(); };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(refetchOpenThreads, 30000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [user?.id, thread?.id, refetchOpenThreads]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    const body = (input || '').trim();
    if (!body || !user?.id) return;
    if (!thread) {
      const { data: t, error } = await customerSupportOperations.getOrCreateThread(user.id, pendingOrderId ?? null);
      if (error || !t) return;
      setThread(t);
      setHasOpenThreads(true);
      await customerSupportOperations.sendMessage(t.id, user.id, body);
      setInput('');
      const { data: msgs } = await customerSupportOperations.getMessages(t.id);
      setMessages(msgs ?? []);
      scrollToBottom();
      return;
    }
    setSending(true);
    setInput('');
    const { error } = await customerSupportOperations.sendMessage(thread.id, user.id, body);
    setSending(false);
    if (error) setInput(body);
    else {
      const { data: msgs } = await customerSupportOperations.getMessages(thread.id);
      setMessages(msgs ?? []);
      scrollToBottom();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  if (!isAuthenticated || !user) return null;
  if (!hasOpenThreads && !isOpen) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[100] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Support chat"
      >
        <Icon name="Headphones" size={24} />
      </button>
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[100] flex w-[min(100vw-2rem,380px)] flex-col rounded-xl border border-border bg-card shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="Headphones" size={18} className="text-primary" />
              Support
            </h3>
            <button
              type="button"
              onClick={handleClose}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
          <p className="px-4 pb-2 text-xs text-muted-foreground border-b border-border">
            We&apos;ll respond within 1–2 business days. This chat disappears once your inquiry is resolved.
          </p>
          <div className="flex flex-1 flex-col min-h-[280px] max-h-[50vh]">
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : messages.length === 0 && !thread ? (
                <p className="text-sm text-muted-foreground">Send a message to start. We&apos;ll get back to you soon.</p>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.id}
                    className={`rounded-lg p-2 text-sm ${m.sender_type === 'customer' ? 'ml-6 bg-primary/10' : 'mr-6 bg-muted'}`}
                  >
                    <span className="font-medium text-muted-foreground text-xs">{m.sender_type === 'customer' ? 'You' : 'Support'}</span>
                    <p className="text-foreground mt-0.5">{m.body}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatTime(m.created_at)}</p>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="border-t border-border p-3 flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Type your message..."
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button size="sm" onClick={handleSend} disabled={sending || !input.trim()}>
                {sending ? '…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SupportChatbotWrapper;
